import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Settings, 
  LogOut, 
  ChevronDown,
  User,
  Palette,
  Eye,
  Building,
  Crown,
  Shield
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/UnifiedDashboardLayout.css';

const UnifiedDashboardLayout = ({ 
  dashboardType, 
  navItems, 
  logoText, 
  logoIcon: LogoIcon,
  children,
  context = {},
  // Controls the initial open/closed state of the sidebar. Defaults to true to preserve existing behavior.
  initialSidebarOpen = true,
  // Optional header component to render at top of sidebar (e.g., org selector)
  sidebarHeader = null,
  // Hide sidebar completely (e.g., on landing/selection pages)
  hideSidebar = false,
  // Organization logo to display in header (when org is selected)
  orgLogo = null,
  // Organization name to display in header
  orgName = null,
  // When true, the sidebar will collapse. When it changes back to false, sidebar re-opens on large screens.
  requestSidebarClosed = false,
}) => {
  console.log('UnifiedDashboardLayout: Received context:', context);
  
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const dropdownRef = useRef(null);
  // Sidebar starts based on prop so dashboards can choose a default
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Track when user explicitly toggles sidebar to prevent auto-expand from overriding
  const manuallyToggledRef = useRef(false);

  // Helper function to add Cloudinary transformation
  const addCloudinaryTransform = (url, transform = 't_square-150/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    // Insert transformation after /upload/
    return url.replace('/upload/', `/upload/${transform}`);
  };

  // Get current user from Supabase auth and profile from users table
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          // Fetch user profile from users table for profile_img
          const { data: profile, error } = await supabase
            .from('users')
            .select('uuid, first_name, last_name, profile_img')
            .eq('uuid', user.id)
            .single();
          
          if (!error && profile) {
            setUserProfile(profile);
          }
          
          // Check if user is admin (role_id 3 in user_roles table)
          const { data: userRoles, error: rolesError } = await supabase
            .from('user_roles')
            .select('role_id')
            .eq('user_id', user.id);
          
          if (!rolesError && userRoles?.some(role => role.role_id === 3)) {
            setIsAdmin(true);
          }
        }
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    
    getCurrentUser();
  }, []);

  // Handle responsive behavior - sidebar hides below 1400px
  useEffect(() => {
    const handleResize = () => {
      const small = window.innerWidth < 1400;
      setIsSmallScreen(small);

      // Only auto-collapse/expand if user hasn't manually toggled
      if (!manuallyToggledRef.current) {
        setSidebarOpen(!small);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to parent requesting sidebar closed (e.g., for full-screen sections)
  useEffect(() => {
    if (requestSidebarClosed) {
      setSidebarOpen(false);
      manuallyToggledRef.current = true;
    } else if (manuallyToggledRef.current) {
      // Section no longer requests close — reset manual flag and restore sidebar on large screens
      manuallyToggledRef.current = false;
      if (window.innerWidth >= 1400) {
        setSidebarOpen(true);
      }
    }
  }, [requestSidebarClosed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAvatarDropdownOpen(false);
      }
    };

    if (avatarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [avatarDropdownOpen]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    console.log('Mobile menu toggle clicked, current state:', mobileMenuOpen);
    const newState = !mobileMenuOpen;
    setMobileMenuOpen(newState);
    console.log('Mobile menu state set to:', newState);
  };

  const handleSidebarToggle = useCallback(() => {
    manuallyToggledRef.current = true;
    setSidebarOpen(prev => !prev);
  }, []);

  // Enhanced mobile menu toggle with touch event handling
  const handleMobileMenuToggleEnhanced = (event) => {
    // Prevent default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('Enhanced mobile menu toggle clicked', {
      eventType: event?.type,
      target: event?.target,
      currentState: mobileMenuOpen,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    });
    
    const newState = !mobileMenuOpen;
    setMobileMenuOpen(newState);
    console.log('Mobile menu state set to:', newState);
    
    // Force a re-render and log the actual DOM state
    setTimeout(() => {
      const overlay = document.querySelector('.unified-mobile-overlay');
      const menu = document.querySelector('.unified-mobile-menu');
      if (overlay) {
        console.log('Mobile overlay DOM classes:', overlay.className);
        console.log('Mobile overlay computed display:', window.getComputedStyle(overlay).display);
        console.log('Mobile overlay computed visibility:', window.getComputedStyle(overlay).visibility);
        console.log('Mobile overlay computed opacity:', window.getComputedStyle(overlay).opacity);
      }
      if (menu) {
        console.log('Mobile menu DOM classes:', menu.className);
        console.log('Mobile menu computed transform:', window.getComputedStyle(menu).transform);
      }
    }, 100);
  };

  // Function to close mobile menu after navigation
  const handleMobileMenuClose = () => {
    console.log('Closing mobile menu after navigation');
    setMobileMenuOpen(false);
  };

  const toggleAvatarDropdown = () => {
    setAvatarDropdownOpen(!avatarDropdownOpen);
  };

  const navigateToDashboard = (dashboardType) => {
    setAvatarDropdownOpen(false);
    switch (dashboardType) {
      case 'artist':
        navigate('/artist/dashboard');
        break;
      case 'collector':
        navigate('/collector/dashboard');
        break;
      case 'gallery':
        navigate('/gallery/dashboard');
        break;
      case 'curator':
        navigate('/curator/dashboard');
        break;
      case 'admin':
        navigate('/admin/dashboard');
        break;
      default:
        break;
    }
  };

  const getDashboardIcon = (type) => {
    switch (type) {
      case 'artist':
        return <Palette size={16} color="#6c757d" />;
      case 'collector':
        return <Eye size={16} color="#6c757d" />;
      case 'gallery':
        return <Building size={16} color="#6c757d" />;
      case 'curator':
        return <Building size={16} color="#6c757d" />;
      case 'admin':
        return <Crown size={16} color="#6c757d" />;
      default:
        return <User size={16} color="#6c757d" />;
    }
  };

  return (
    <div className="unified-dashboard-container">
      {/* Top Header */}
      <header className="unified-header">
        <div className="unified-header-content">
          <div className="unified-header-left">
            {/* Sidebar Toggle - works on all screen sizes */}
            {!hideSidebar && (
              <button
                onClick={handleSidebarToggle}
                className="unified-sidebar-toggle"
                title={sidebarOpen ? 'Close Menu' : 'Open Menu'}
                style={{ zIndex: 1000 }}
              >
                <Menu size={24} color="#6c757d" />
              </button>
            )}
            
            <div className="unified-logo">
              {orgLogo ? (
                <Link 
                  to={context.selectedOrganization?.slug ? `/curator/organization/${context.selectedOrganization.slug}` : '/curator/dashboard'}
                  className="unified-logo-link"
                >
                  <img 
                    src={addCloudinaryTransform(orgLogo, 't_fitmax-300/')}
                    alt={orgName || 'Organization'}
                    className="unified-org-logo-img"
                  />
                </Link>
              ) : (
                <>
                  {LogoIcon && <LogoIcon size={24} color="#007bff" />}
                  <span className="unified-logo-text">{orgName || logoText}</span>
                </>
              )}
            </div>
          </div>

          <div className="unified-header-right">
            {/* User Avatar & Dropdown */}
            <div className="unified-user-section" ref={dropdownRef}>
              <button
                onClick={toggleAvatarDropdown}
                className="unified-avatar-btn"
                title="User Menu"
              >
                <div className="unified-avatar">
                  {userProfile?.profile_img ? (
                    <img 
                      src={addCloudinaryTransform(userProfile.profile_img, 't_square-150/')} 
                      alt="User Avatar" 
                      className="unified-avatar-img"
                    />
                  ) : user?.user_metadata?.avatar_url ? (
                    <img 
                      src={user.user_metadata.avatar_url} 
                      alt="User Avatar" 
                      className="unified-avatar-img"
                    />
                  ) : (
                    <div className="unified-avatar-placeholder">
                      {user?.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
                <span className="unified-user-name">
                  {userProfile?.first_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                </span>
                <ChevronDown size={16} color="#6c757d" />
              </button>

              {/* Avatar Dropdown */}
              {avatarDropdownOpen && (
                <div className="unified-avatar-dropdown">
                  {/* Organization Switcher - Show organizations user has access to (Curator context) */}
                  {(() => {
                    const organizations = context.organizations || [];
                    const selectedOrganization = context.selectedOrganization;
                    const onSelectOrganization = context.onSelectOrganization;
                    
                    return organizations.length > 0 && (
                      <div className="unified-dashboard-switcher">
                        <div className="unified-dropdown-section-label">Switch Organization</div>
                        {organizations.map(org => (
                          <button
                            key={org.id || org.organization_id}
                            onClick={() => {
                              if (onSelectOrganization) {
                                onSelectOrganization(org);
                              }
                              setAvatarDropdownOpen(false);
                            }}
                            className={`unified-dashboard-option ${selectedOrganization?.id === org.id || selectedOrganization?.organization_id === org.organization_id ? 'active' : ''}`}
                          >
                            <Building size={16} color="#6c757d" />
                            <span>{org.name || org.organization_name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Curator Organizations - Show when in Admin dashboard for quick access */}
                  {dashboardType === 'admin' && (() => {
                    const curatorOrganizations = context.curatorOrganizations || [];
                    
                    return curatorOrganizations.length > 0 && (
                      <div className="unified-dashboard-switcher curator-orgs">
                        <div className="unified-dropdown-section-label">Curator Access</div>
                        {curatorOrganizations.map(org => (
                          <Link
                            key={org.id}
                            to={`/curator/organization/${org.slug}`}
                            onClick={() => setAvatarDropdownOpen(false)}
                            className="unified-dashboard-option"
                          >
                            <Building size={16} color="#10b981" />
                            <span>{org.name}</span>
                          </Link>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Admin Dashboard Link - Only for admins, show when NOT in admin dashboard */}
                  {isAdmin && dashboardType !== 'admin' && (
                    <div className="unified-admin-section">
                      <Link 
                        to="/admin/dashboard" 
                        className="unified-dropdown-item admin-link"
                        onClick={() => setAvatarDropdownOpen(false)}
                      >
                        <Shield size={16} color="#7c3aed" />
                        <span>Admin Dashboard</span>
                      </Link>
                    </div>
                  )}

                  {/* Curator Dashboard Link - Show when in Admin dashboard */}
                  {dashboardType === 'admin' && (
                    <div className="unified-admin-section">
                      <Link 
                        to="/curator/dashboard" 
                        className="unified-dropdown-item admin-link"
                        onClick={() => setAvatarDropdownOpen(false)}
                      >
                        <Building size={16} color="#10b981" />
                        <span>Curator Dashboard</span>
                      </Link>
                    </div>
                  )}

                  {/* Settings & Sign Out */}
                  <div className="unified-user-actions">
                    <Link 
                      to="/curator/settings" 
                      className="unified-dropdown-item"
                      onClick={() => setAvatarDropdownOpen(false)}
                    >
                      <Settings size={16} color="#6c757d" />
                      <span>Settings</span>
                    </Link>
                    <button onClick={handleLogout} className="unified-dropdown-item">
                      <LogOut size={16} color="#6c757d" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className={`unified-main-content ${hideSidebar ? 'no-sidebar' : ''}`}>
        {/* Sidebar Backdrop - shown when sidebar is open on smaller screens */}
        {!hideSidebar && isSmallScreen && sidebarOpen && (
          <div 
            className="unified-sidebar-backdrop" 
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {/* Sidebar - hidden when hideSidebar is true */}
        {!hideSidebar && (
        <aside className={`unified-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          {/* Render optional sidebar header (e.g., organization selector) */}
          {sidebarHeader && sidebarOpen && sidebarHeader}
          <nav className="unified-sidebar-nav">
            {navItems.map((item) => {
              // Support both routing (href) and state-based (onClick) navigation
              if (item.onClick) {
                // State-based navigation - determine active state from parent context
                const isActive = context.activeSection === item.section;
                return (
                  <button
                    key={item.name || item.title || item.section}
                    onClick={item.onClick}
                    className={`unified-nav-item ${isActive ? 'active' : ''}`}
                  >
                    <item.icon size={20} color={isActive ? "#ffffff" : "#6c757d"} />
                    <span className="unified-nav-text">{item.name || item.title}</span>
                  </button>
                );
              } else {
                // Route-based navigation
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name || item.title || item.href}
                    to={item.href}
                    className={`unified-nav-item ${isActive ? 'active' : ''}`}
                  >
                    <item.icon size={20} color={isActive ? "#ffffff" : "#6c757d"} />
                    <span className="unified-nav-text">{item.name || item.title}</span>
                  </Link>
                );
              }
            })}
          </nav>

          {/* Bottom Sidebar Actions - removed, now in dropdown menu */}
        </aside>
        )}

        {/* Main Content */}
        <main className="unified-main">
          {children ? children : (() => {
            // Use context user if provided, otherwise use local user
            const contextToPass = { user: context.user || user, setUser, ...context };
            console.log('UnifiedDashboardLayout: Passing context to Outlet:', contextToPass);
            return <Outlet context={contextToPass} />;
          })()}
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="unified-mobile-overlay" onClick={handleMobileMenuClose}>
          <div className="unified-mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="unified-mobile-menu-header">
              <span className="unified-mobile-menu-title">Menu</span>
              <button onClick={handleMobileMenuClose} className="unified-mobile-close">
                <X size={24} color="#6c757d" />
              </button>
            </div>
            
            {/* Render sidebar header (e.g., organization selector) in mobile menu */}
            {sidebarHeader && (
              <div className="unified-mobile-sidebar-header">
                {sidebarHeader}
              </div>
            )}
            
            <nav className="unified-mobile-nav">
              {navItems.map((item) => {
                if (item.onClick) {
                  // State-based navigation
                  return (
                    <button
                      key={item.name || item.title || item.section}
                      onClick={() => {
                        item.onClick();
                        handleMobileMenuClose(); // Close menu after navigation
                      }}
                      className="unified-mobile-nav-item"
                    >
                      <item.icon size={20} color="#6c757d" />
                      <span>{item.name || item.title}</span>
                    </button>
                  );
                } else {
                  // Route-based navigation
                  return (
                    <Link
                      key={item.name || item.title || item.href}
                      to={item.href}
                      className="unified-mobile-nav-item"
                      onClick={handleMobileMenuClose} // Close menu after navigation
                    >
                      <item.icon size={20} color="#6c757d" />
                      <span>{item.name || item.title}</span>
                    </Link>
                  );
                }
              })}
            </nav>

            <div className="unified-mobile-actions">
              {/* Sign Out removed as it is available in the top right user menu */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedDashboardLayout;
